import React, { useEffect, useMemo, useState } from "react";
import { Button } from "@mui/material";
import AddShoppingCartIcon from "@mui/icons-material/AddShoppingCart";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { FREE_SHIPPING_FROM, PRODUCTS, PROMO_CODES, SHIPPING_COST, VAT_RATE } from "./products";
import "./ShoppingCart.css";

const STORAGE_KEY = "sts-shopping-cart";

type Cart = Record<number, number>;

function loadCart(): Cart {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        return raw ? JSON.parse(raw) : {};
    } catch (e) {
        return {};
    }
}

const money = (value: number) =>
    new Intl.NumberFormat("en-US", { style: "currency", currency: "EUR" }).format(value);

export function ShoppingCart() {
    const [cart, setCart] = useState<Cart>(loadCart);
    const [category, setCategory] = useState("All");
    const [search, setSearch] = useState("");
    const [promoInput, setPromoInput] = useState("");
    const [promo, setPromo] = useState<string | null>(null);
    const [promoError, setPromoError] = useState("");
    const [orderPlaced, setOrderPlaced] = useState<string | null>(null);

    useEffect(() => {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(cart));
        } catch (e) {
            // Ignore - persistence is a convenience only
        }
    }, [cart]);

    const categories = useMemo(() => {
        const list = ["All"];
        PRODUCTS.forEach(p => {
            if (list.indexOf(p.category) === -1) {
                list.push(p.category);
            }
        });
        return list;
    }, []);

    const visibleProducts = PRODUCTS.filter(
        p =>
            (category === "All" || p.category === category) &&
            p.name.toLowerCase().includes(search.trim().toLowerCase())
    );

    const lines = PRODUCTS.filter(p => cart[p.id] > 0).map(p => ({ product: p, qty: cart[p.id] }));
    const itemCount = lines.reduce((sum, l) => sum + l.qty, 0);
    const subtotal = lines.reduce((sum, l) => sum + l.qty * l.product.price, 0);
    const discount = promo ? subtotal * PROMO_CODES[promo] : 0;
    const afterDiscount = subtotal - discount;
    const shipping = itemCount === 0 || afterDiscount >= FREE_SHIPPING_FROM ? 0 : SHIPPING_COST;
    const vat = afterDiscount * VAT_RATE;
    const total = afterDiscount + vat + shipping;
    const shippingProgress = Math.min(100, (afterDiscount / FREE_SHIPPING_FROM) * 100);

    const setQty = (id: number, qty: number) => {
        setOrderPlaced(null);
        setCart(current => {
            const next = { ...current };
            if (qty <= 0) {
                delete next[id];
            } else {
                next[id] = Math.min(99, qty);
            }
            return next;
        });
    };

    const applyPromo = () => {
        const code = promoInput.trim().toUpperCase();
        if (PROMO_CODES[code] !== undefined) {
            setPromo(code);
            setPromoError("");
        } else {
            setPromo(null);
            setPromoError("Invalid code. Try SMART10");
        }
    };

    const checkout = () => {
        setOrderPlaced(`STS-${Math.floor(100000 + Math.random() * 900000)}`);
        setCart({});
        setPromo(null);
        setPromoInput("");
    };

    return (
        <div className="cart">
            <section className="cart-shop">
                <div className="cart-filters">
                    <input
                        type="search"
                        placeholder="Search products…"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                    />
                    <div className="cart-chips">
                        {categories.map(c => (
                            <button key={c} className={c === category ? "active" : ""} onClick={() => setCategory(c)}>
                                {c}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="cart-products">
                    {visibleProducts.map(p => {
                        const qty = cart[p.id] || 0;
                        return (
                            <div key={p.id} className="cart-product">
                                <div className="cart-product-icon" style={{ background: p.tint }}>
                                    <span>{p.icon}</span>
                                    {qty > 0 && <span className="cart-product-badge">{qty}</span>}
                                </div>
                                <div className="cart-product-info">
                                    <small>{p.category}</small>
                                    <h4>{p.name}</h4>
                                    <strong>{money(p.price)}</strong>
                                </div>
                                {qty === 0 ? (
                                    <Button
                                        size="small"
                                        variant="contained"
                                        className="cart-add-btn"
                                        startIcon={<AddShoppingCartIcon fontSize="small" />}
                                        onClick={() => setQty(p.id, 1)}
                                    >
                                        Add
                                    </Button>
                                ) : (
                                    <QtyStepper qty={qty} onChange={n => setQty(p.id, n)} />
                                )}
                            </div>
                        );
                    })}
                    {visibleProducts.length === 0 && <p className="cart-muted">No products match your search.</p>}
                </div>
            </section>

            <aside className="cart-summary">
                <h3>
                    <ShoppingCartOutlinedIcon /> Your cart
                    <span className="cart-count">{itemCount}</span>
                </h3>

                {orderPlaced && (
                    <div className="cart-success">
                        <CheckCircleIcon />
                        <div>
                            <strong>Order placed!</strong>
                            <span>Order #{orderPlaced} (demo - nothing was charged)</span>
                        </div>
                    </div>
                )}

                {lines.length === 0 ? (
                    !orderPlaced && <p className="cart-muted">Your cart is empty. Add something from the list.</p>
                ) : (
                    <>
                        <ul className="cart-lines">
                            {lines.map(({ product, qty }) => (
                                <li key={product.id}>
                                    <span className="cart-line-icon" style={{ background: product.tint }}>{product.icon}</span>
                                    <div className="cart-line-info">
                                        <span>{product.name}</span>
                                        <small>{money(product.price)} each</small>
                                    </div>
                                    <QtyStepper qty={qty} onChange={n => setQty(product.id, n)} small />
                                    <strong className="cart-line-total">{money(product.price * qty)}</strong>
                                    <button className="cart-remove" onClick={() => setQty(product.id, 0)} aria-label={`Remove ${product.name}`}>
                                        <DeleteOutlineIcon fontSize="small" />
                                    </button>
                                </li>
                            ))}
                        </ul>

                        <div className="cart-shipping">
                            {shipping === 0 ? (
                                <span>🎉 You get <strong>free shipping</strong>!</span>
                            ) : (
                                <span>Add <strong>{money(FREE_SHIPPING_FROM - afterDiscount)}</strong> more for free shipping</span>
                            )}
                            <div className="cart-shipping-bar">
                                <div style={{ width: `${shippingProgress}%` }} />
                            </div>
                        </div>

                        <div className="cart-promo">
                            <input
                                placeholder="Promo code"
                                value={promoInput}
                                onChange={e => setPromoInput(e.target.value)}
                                onKeyDown={e => e.key === "Enter" && applyPromo()}
                            />
                            <Button size="small" variant="outlined" onClick={applyPromo}>Apply</Button>
                        </div>
                        {promoError && <small className="cart-error">{promoError}</small>}
                        {promo && <small className="cart-ok">Code {promo} applied (-{PROMO_CODES[promo] * 100}%)</small>}

                        <dl className="cart-totals">
                            <dt>Subtotal</dt><dd>{money(subtotal)}</dd>
                            {discount > 0 && (<><dt>Discount</dt><dd className="cart-ok">-{money(discount)}</dd></>)}
                            <dt>Shipping</dt><dd>{shipping === 0 ? "Free" : money(shipping)}</dd>
                            <dt>VAT ({VAT_RATE * 100}%)</dt><dd>{money(vat)}</dd>
                            <dt className="cart-grand">Total</dt><dd className="cart-grand">{money(total)}</dd>
                        </dl>

                        <Button variant="contained" fullWidth className="cart-checkout" onClick={checkout}>
                            Checkout
                        </Button>
                        <button className="cart-clear" onClick={() => setCart({})}>Clear cart</button>
                    </>
                )}
            </aside>
        </div>
    );
}

function QtyStepper({ qty, onChange, small }: { qty: number; onChange: (qty: number) => void; small?: boolean }) {
    return (
        <div className={`cart-stepper ${small ? "small" : ""}`}>
            <button onClick={() => onChange(qty - 1)} aria-label="Decrease">−</button>
            <span>{qty}</span>
            <button onClick={() => onChange(qty + 1)} aria-label="Increase">+</button>
        </div>
    );
}
